/**
 * Render one collapsible Collection panel.
 */
import {GOLD_300, GOLD_600} from '@wandb/weave/common/css/globals.styles';
import {regexMatchHighlight} from '@wandb/weave/common/util/fuzzyMatch';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Panel2Loader} from '@wandb/weave/components/Panel2/PanelComp';
// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {useRefreshAllNodes} from '@wandb/weave/react';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useHistory} from 'react-router-dom';
import TimeAgo from 'react-timeago';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../../config';
import {
  useUpdateArtifactCollectionMutation,
  useUpdateArtifactPortfolioMutation,
} from '../../../generated/graphql';
import {useHandleScroll} from '../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../../services/analytics';
import {noop} from '../../../state/views/actions';
import {
  PremiumFeatureTooltip,
  useIsAutomationEnabled,
} from '../../../util/accounts/pricing';
import {propagateErrorsContext} from '../../../util/errors';
import {trackCreateTriggerClicked} from '../../../util/navigation';
import {capitalizeFirstLetter} from '../../../util/string';
import {maybePluralize} from '../../../util/uihelpers';
import * as urls from '../../../util/urls';
import {CreateTriggerDrawerForCollection} from '../../ArtifactCollectionSettings/CreateTriggerModal';
import {
  DeleteArtifactModal,
  RenameArtifactModal,
} from '../../ArtifactSidebar/ArtifactSidebarActions';
import {EntityChip} from '../../Launch/Queues/EntityChip';
import {useTriggers} from '../../ModelRegistry/hooks';
import {ArtifactsMembershipsTableWrapper} from '../../panels.domain/artifact/ArtifactsMembershipsTableWrapper';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {createDisplayTag, TagAssignment} from '../../TagAssignment';
import {
  useFetchArtifactCollectionSupplemental,
  useIsProjectReadOnly,
} from '../common/hooks';
import {
  RegistriesSearchContext,
  SingleRegistrySearchContext,
} from '../common/RegistriesSearchContextProvider';
import {
  CardCollectionType,
  RegistrySearchCollectionType,
} from '../common/types';
import {REGISTRY_PROJECT_PREFIX, resolveRegistryUrl} from '../common/utils';
import {AddArtifactDrawer} from '../workflows/CreateRegisteredCollectionDrawer/AddArtifactDrawer/AddArtifactDrawer';
import * as S from './ArtifactCollectionCard.styles';
import {CollectionType} from './types';
import {ZeroVersions} from './ZeroVersions';

type DefaultCardCollection = {
  type: CardCollectionType.DEFAULT;
  collection: CollectionType;
};

export type RegistrySearchCardCollection = {
  type: CardCollectionType.REGISTRY_SEARCH;
  collection: RegistrySearchCollectionType;
};

export type SingleRegistryCardCollection = {
  type: CardCollectionType.SINGLE_REGISTRY;
  collection: RegistrySearchCollectionType;
};

type CollectionInfo =
  | DefaultCardCollection
  | RegistrySearchCardCollection
  | SingleRegistryCardCollection;

type ArtifactCollectionCardProps = {
  registryName: string;
  collectionInfo: CollectionInfo;
  showEntity?: boolean;
  refetchCollections?: () => void;
  legacySearchFilter?: string; // Avoid this in new code. Originally used when search/filtering logic was mostly client side.
  protectedAliases?: string[];
};

// Note: ArtifactSidebarActions needs a larger refactor around
// how the modal types and modal states are handled. For now,
// adding an enum for ModalType here
enum ArtifactActionModalType {
  RENAME,
  DELETE,
  ADD_TRIGGER,
  LINK_ARTIFACT,
}

export const ArtifactCollectionCard = ({
  registryName,
  collectionInfo,
  showEntity,
  refetchCollections,
  legacySearchFilter,
  protectedAliases,
}: ArtifactCollectionCardProps) => {
  const collection = collectionInfo.collection;
  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);

  const searchContext = useMemo(() => {
    return collectionInfo.type === CardCollectionType.SINGLE_REGISTRY
      ? SingleRegistrySearchContext
      : RegistriesSearchContext;
  }, [collectionInfo.type]);

  const {searchFilter, searchFilterRegex} = useContext(searchContext);

  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const isOrgRegistry = collection.projectName.startsWith(
    REGISTRY_PROJECT_PREFIX
  );
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(collection.entityName, collection.projectName);

  const {
    // There is a lag between when a project is deleted and when the artifact collections
    // get deleted due to our data retention policy. Due to this, we're able to fetch
    // portfolios from deleted projects.
    // This kind of additional check is required since while fetching the project for an
    // artifact collection, we are able to fetch deleted projects too.
    isProjectDeleted,
    numVersions,
    numConsumers,
    loading: supplementalLoading,
  } = useFetchArtifactCollectionSupplemental(collection.collectionId);

  // State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [modalType, setModalType] = useState<
    ArtifactActionModalType | undefined
  >();
  const [modalOpen, setModalOpen] = useState(false);
  const isDeleteOpen = (deleteOpen: boolean) => {
    if (deleteOpen) {
      setModalType(ArtifactActionModalType.DELETE);
    } else {
      setModalType(undefined);
    }
    setModalOpen(deleteOpen);
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);

  // Auto-expand matching collections if there's an active, non-empty search filter
  // or if the user wants to see all versions
  useEffect(() => {
    setIsExpanded(
      collectionInfo.type === CardCollectionType.DEFAULT
        ? !!legacySearchFilter?.trim()
        : collectionInfo.collection.isArtifactMatch || showAllVersions
    );
  }, [legacySearchFilter, collectionInfo, showAllVersions]);

  // if the search filter changes, we want to go back to the default view
  useEffect(() => {
    setShowAllVersions(false);
  }, [searchFilter]);

  const onToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // track collection interactions if on registries homepage
  const trackCollectionInteraction =
    collectionInfo.type === CardCollectionType.REGISTRY_SEARCH
      ? (action: string) => {
          Analytics.track('Registry search interaction', {
            action,
            entityName: collection.entityName,
          });
        }
      : noop;

  // For continuity, don't lock collections on legacy model registry
  const isLocked = isOrgRegistry && searchFilter != null;
  const collectionStyle = {
    cursor: isLocked ? 'default' : 'pointer',
  };

  const collectionClickHandler = isLocked
    ? undefined
    : {
        onClick: () => {
          onToggleExpanded();
          trackCollectionInteraction('collection interaction');
        },
      };

  // Hooks
  const [updateArtifactPortfolio] = useUpdateArtifactPortfolioMutation({
    context: propagateErrorsContext(),
  });
  const [updateArtifactSequence] = useUpdateArtifactCollectionMutation({
    context: propagateErrorsContext(),
  });
  const refreshNodes = useRefreshAllNodes();
  const history = useHistory();

  const selectionPath = useMemo(() => {
    return {
      entityName: collection?.entityName,
      projectName: collection?.projectName,
      collectionName: collection?.collectionName,
    };
  }, [collection]);

  const collectionTags = useMemo(() => {
    return collectionInfo.type === CardCollectionType.DEFAULT
      ? collectionInfo.collection.collectionTags
      : collectionInfo.collection.tags;
  }, [collectionInfo.collection, collectionInfo.type]);

  const updatedAt =
    collectionInfo.type === CardCollectionType.REGISTRY_SEARCH ||
    collectionInfo.type === CardCollectionType.SINGLE_REGISTRY
      ? new Date(collectionInfo.collection.lastUpdatedAt)
      : collectionInfo.collection.lastMembershipCreatedAt
        ? new Date(collectionInfo.collection.lastMembershipCreatedAt)
        : new Date(collectionInfo.collection.createdAt);

  const triggersData = useTriggers(
    collection?.entityName,
    collection?.projectName,
    collection?.collectionId
  );

  const onHistorySelectQuickView = useCallback(
    (versionIndex: number) => {
      if (versionIndex != null) {
        history.push(
          resolveRegistryUrl(
            isOrgRegistry,
            accountOrgName,
            registryName,
            selectionPath,
            collection?.entityName,
            'membership',
            'v' + versionIndex
          )
        );
      }
    },
    [
      history,
      selectionPath,
      collection?.entityName,
      registryName,
      isOrgRegistry,
      accountOrgName,
    ]
  );
  const scrolled = useHandleScroll();

  const isArtifactMatch = useMemo(() => {
    if (collectionInfo.type === CardCollectionType.DEFAULT) {
      return (
        !!legacySearchFilter &&
        !collectionMatchesSearch(collectionInfo.collection, legacySearchFilter)
      );
    }
    return collectionInfo.collection.isArtifactMatch;
  }, [collectionInfo.collection, collectionInfo.type, legacySearchFilter]);

  if (
    !collection ||
    triggersData.loading ||
    isProjectReadOnlyLoading ||
    supplementalLoading
  ) {
    return <Panel2Loader />;
  }
  if (isProjectDeleted) {
    return null;
  }
  return (
    <>
      <S.CollectionWrapper>
        <S.Collection
          {...collectionClickHandler}
          style={collectionStyle}
          data-test={`card-${collection.collectionName}`}>
          {!isLocked && (
            <S.CollectionExpandWrapper>
              <Button
                variant="ghost"
                size="medium"
                icon={isExpanded ? 'chevron-down' : 'chevron-next'}
                data-test="collection-card-expand-button"
                onClick={() => {
                  onToggleExpanded();
                  trackCollectionInteraction('collection interaction');
                }}
              />
            </S.CollectionExpandWrapper>
          )}
          <S.CollectionHeader>
            <S.CollectionNamePanel>
              <S.CollectionName
                {...collectionClickHandler}
                style={collectionStyle}
                title={collection.collectionName}>
                {(collectionInfo.type === CardCollectionType.REGISTRY_SEARCH ||
                  collectionInfo.type === CardCollectionType.SINGLE_REGISTRY) &&
                searchFilterRegex
                  ? regexMatchHighlight(
                      collection.collectionName,
                      searchFilterRegex,
                      {
                        backgroundColor: GOLD_300,
                      }
                    )
                  : collection.collectionName}
              </S.CollectionName>
              {showEntity && (
                <S.CollectionEntity>
                  <EntityChip name={collection.entityName} />
                </S.CollectionEntity>
              )}
              <S.CollectionTags>
                {collectionTags.map((tag: any) => (
                  <TagAssignment
                    key={tag.tagCategoryName + tag.name}
                    tag={tag}
                    showColor={true}
                    size="large"
                    canDelete={false}
                    highlightedText={searchFilter}
                  />
                ))}
              </S.CollectionTags>
              <S.CollectionActions onClick={e => e.stopPropagation()}>
                <Button
                  className="view-details"
                  size="medium"
                  variant="ghost"
                  style={{marginRight: '8px'}}
                  onClick={() =>
                    history.push(
                      resolveRegistryUrl(
                        isOrgRegistry,
                        accountOrgName,
                        registryName,
                        selectionPath,
                        collection.entityName
                      )
                    )
                  }>
                  View details
                </Button>
                {!readOnly && (
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      trackCollectionInteraction('view collection details');
                    }}>
                    <DropdownMenu.Root
                      open={isMenuOpen}
                      onOpenChange={setIsMenuOpen}>
                      <DropdownMenu.Trigger>
                        <Button
                          data-test="collection-card-overflow-menu"
                          size="medium"
                          variant="ghost"
                          icon="overflow-horizontal"
                          active={isMenuOpen}
                        />
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content align="end">
                          <DropdownMenu.Group>
                            <DropdownMenu.Item
                              onClick={() =>
                                history.push(
                                  resolveRegistryUrl(
                                    isOrgRegistry,
                                    accountOrgName,
                                    registryName,
                                    selectionPath,
                                    collection.entityName
                                  )
                                )
                              }>
                              <Icon name="model" />
                              View details
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              onClick={() => {
                                setModalType(ArtifactActionModalType.RENAME);
                                setModalOpen(true);
                              }}>
                              <Icon name="pencil-edit" />
                              Rename
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              disabled={!hasAutomationAccess}
                              onClick={() => {
                                setModalType(
                                  ArtifactActionModalType.ADD_TRIGGER
                                );
                                setModalOpen(true);
                                trackCreateTriggerClicked(
                                  'New registry UI - collection card overflow menu'
                                );
                              }}>
                              <Icon name="trigger-alt" />
                              <>
                                Create automation
                                <PremiumFeatureTooltip
                                  isPremiumFeatureDisabled={
                                    !hasAutomationAccess
                                  }
                                />
                              </>
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              onClick={() => {
                                setModalType(
                                  ArtifactActionModalType.LINK_ARTIFACT
                                );
                                setModalOpen(true);
                              }}>
                              <Icon name="add-new" />
                              Link new version
                            </DropdownMenu.Item>
                          </DropdownMenu.Group>
                          <DropdownMenu.Separator />
                          <DropdownMenu.Item
                            onClick={() => {
                              setModalType(ArtifactActionModalType.DELETE);
                              setModalOpen(true);
                            }}>
                            <Icon name="delete" />
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>
                )}
              </S.CollectionActions>
            </S.CollectionNamePanel>
            <S.CollectionOverviewPanel className="flex-start flex flex-row">
              {collectionInfo.type === CardCollectionType.REGISTRY_SEARCH && (
                // Within a single registry / legacy model registry page, we can skip the registry name per card
                <S.CollectionOverviewItem
                  className="min-w-0 min-w-0 max-w-fit shrink-[0.5] grow-0 cursor-pointer items-center font-semibold text-teal-600 hover:text-teal-500"
                  onClick={() => {
                    trackCollectionInteraction(
                      'view registry from collection card'
                    );
                    history.push(
                      urls.globalRegistry(accountOrgName, registryName)
                    );
                  }}>
                  <S.CollectionOverviewIcon name="registries" />
                  <span
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                    title={registryName}>
                    {capitalizeFirstLetter(registryName)}
                  </span>
                </S.CollectionOverviewItem>
              )}
              {updatedAt && (
                <S.CollectionOverviewItem className="flex-none">
                  <S.CollectionOverviewIcon name="recent-clock" />
                  Last updated{' '}
                  <TimeAgo date={updatedAt} style={{marginLeft: '4px'}} />
                </S.CollectionOverviewItem>
              )}
              <S.CollectionOverviewItem className="flex min-w-0 max-w-full shrink-[2] grow-0 items-center">
                <S.CollectionOverviewIcon name="cube-container" />
                <span
                  style={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                  title={collection.artifactTypeName}>
                  {collection.artifactTypeName}
                </span>
              </S.CollectionOverviewItem>
              {numVersions > 0 ? (
                <Tooltip
                  content={maybePluralize(numVersions, 'version')}
                  trigger={
                    <S.CollectionOverviewItem
                      hover={true}
                      className="flex-none">
                      <S.CollectionOverviewIcon name="versions-layers" />
                      {numVersions}
                    </S.CollectionOverviewItem>
                  }
                  position="bottom left"
                />
              ) : (
                <S.CollectionOverviewItem className="flex-none">
                  <S.CollectionOverviewIcon
                    name="info"
                    style={{color: `${GOLD_600}`}}
                  />
                  <span style={{color: `${GOLD_600}`}}>No versions added</span>
                </S.CollectionOverviewItem>
              )}
              {numConsumers !== 0 && (
                <Tooltip
                  content={maybePluralize(numConsumers, 'consumer')}
                  trigger={
                    <S.CollectionOverviewItem
                      hover={true}
                      className="flex-none">
                      <S.CollectionOverviewIcon name="user-profile-personal" />
                      {numConsumers}
                    </S.CollectionOverviewItem>
                  }
                  position="bottom left"
                />
              )}
              {triggersData?.triggers?.length > 0 && (
                <Tooltip
                  content={maybePluralize(
                    triggersData?.triggers?.length,
                    'automation'
                  )}
                  trigger={
                    <S.CollectionOverviewItem
                      hover={true}
                      className="flex-none">
                      <S.CollectionOverviewIcon name="trigger-alt" />
                      {triggersData?.triggers?.length}
                    </S.CollectionOverviewItem>
                  }
                  position="bottom left"
                />
              )}
              {isOrgRegistry && searchFilter && (
                <S.CollectionOverviewItem className="flex-none items-start">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => {
                      setShowAllVersions(!showAllVersions);
                    }}>
                    {showAllVersions
                      ? 'Hide all versions'
                      : 'Show all versions'}
                  </Button>
                </S.CollectionOverviewItem>
              )}
            </S.CollectionOverviewPanel>
          </S.CollectionHeader>
        </S.Collection>
        {isExpanded &&
          (numVersions > 0 ? (
            <S.ItemTableWrapper>
              <ArtifactsMembershipsTableWrapper
                onRowClick={onHistorySelectQuickView}
                border={`0px`}
                artifactCollectionName={
                  collectionInfo.collection.collectionName
                }
                projectName={collectionInfo.collection.projectName}
                entityName={collectionInfo.collection.entityName}
                artifactCollectionId={collectionInfo.collection.collectionId}
                isArtifactMatch={isArtifactMatch}
                initialMemberships={
                  collectionInfo.type === CardCollectionType.DEFAULT
                    ? undefined
                    : collectionInfo.collection.initialMemberships
                }
                cardCollectionType={collectionInfo.type}
                isPortfolio={collectionInfo.collection.isPortfolio}
                protectedAliases={protectedAliases}
                showAllVersions={showAllVersions}
                legacySearchFilter={
                  isArtifactMatch ? legacySearchFilter : undefined
                }
              />
            </S.ItemTableWrapper>
          ) : (
            <S.ItemTableWrapper>
              <S.EmptyCollection>
                <ZeroVersions
                  entityName={collection.entityName}
                  projectName={collection.projectName}
                  artifactTypeName={collection.artifactTypeName}
                  collectionName={collection.collectionName}
                />
              </S.EmptyCollection>
            </S.ItemTableWrapper>
          ))}
        {modalType === ArtifactActionModalType.RENAME && (
          <RenameArtifactModal
            name={collection.collectionName}
            modalOpen={modalOpen}
            onRenameArtifact={async newName => {
              try {
                if (collection.isPortfolio) {
                  await updateArtifactPortfolio({
                    variables: {
                      artifactPortfolioID: collection.collectionId,
                      name: newName,
                    },
                  });
                } else {
                  await updateArtifactSequence({
                    variables: {
                      artifactSequenceID: collection.collectionId,
                      name: newName,
                    },
                  });
                }
              } catch (error) {
                console.error(error);
                toast(
                  'Something went wrong while trying to rename this collection.'
                );
              }
              setModalType(undefined);
              setModalOpen(false);
              await refreshNodes();
              await refetchCollections?.();
            }}
            onClose={() => {
              setModalType(undefined);
              setModalOpen(false);
            }}
          />
        )}
        {modalType === ArtifactActionModalType.DELETE && (
          <DeleteArtifactModal
            id={collection.collectionId}
            entityName={collection.entityName}
            projectName={collection.projectName}
            artifactTypeName={collection.artifactTypeName}
            artifactCollectionName={collection.collectionName}
            isPortfolio={collection.isPortfolio}
            isModalOpen={modalOpen}
            refetchProjectArtifacts={async () => {
              await refreshNodes();
              await refetchCollections?.();
            }}
            setIsDeleteModalOpen={isDeleteOpen}
          />
        )}
        {modalType === ArtifactActionModalType.ADD_TRIGGER &&
          collection.isPortfolio && (
            <CreateTriggerDrawerForCollection
              open={modalOpen}
              onClose={() => {
                setModalType(undefined);
                setModalOpen(false);
              }}
              projectName={collection.projectName}
              entityName={collection.entityName}
              projectID={collection.projectId}
              artifactCollectionID={collection.collectionId}
              artifactCollectionName={collection.collectionName}
            />
          )}
        {modalType === ArtifactActionModalType.LINK_ARTIFACT &&
          collection.isPortfolio &&
          !supplementalLoading && (
            <AddArtifactDrawer
              open={modalOpen}
              closeDrawer={() => {
                setModalType(undefined);
                setModalOpen(false);
              }}
              defaultProjectName={
                collection.projectName.startsWith(REGISTRY_PROJECT_PREFIX)
                  ? collection.projectName
                  : `${collection.artifactTypeName}-registry`
              }
              collection={{
                ...collection,
                name: collection.collectionName,
                id: collection.collectionId,
                numVersions,
                description: '',
                tags:
                  collectionInfo.type === CardCollectionType.REGISTRY_SEARCH ||
                  collectionInfo.type === CardCollectionType.SINGLE_REGISTRY
                    ? collectionInfo.collection.tags.map(tag =>
                        createDisplayTag(tag)
                      )
                    : collectionInfo.collection.tags,
              }}
              scrollWidth={scrolled.scrollHeight}
              refetchCollections={refetchCollections}
            />
          )}
      </S.CollectionWrapper>
    </>
  );
};

/**
 * Returns `true` if the artifact **collection** itself (independent of any artifact it contains) matches the search filter.
 */
export function collectionMatchesSearch(
  collectionInfo: CollectionType,
  searchFilter: string
) {
  const searchTarget = searchFilter.toLowerCase();
  return (
    collectionInfo.collectionName.toLowerCase().includes(searchTarget) ||
    collectionInfo.artifactTypeName.toLowerCase().includes(searchTarget) ||
    collectionInfo.collectionTags.some(tag =>
      tag.name.toLowerCase().includes(searchTarget)
    )
  );
}
